import React, { useState, useEffect } from 'react';
import { Company, Location, USER_ROLE } from '../Types';
import useGetData from '../Hooks/useGetData';
import LocationForm, { LocationFormInput } from './Location/LocationForm';
import LocationBulkImport from './Location/LocationBulkImport';
import {
  AddButton,
  BulkImportLink,
  CancelButton,
  CloseButton,
  DeleteConfirmButton,
  EditButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  PageContainer,
  TealSwitch,
} from '../Styles/AppStyles';
import apiClient from '../Services/ApiClient';
import { decode } from '../Services/DecodeToken';
import { useSnackbar } from '../Context/SnackbarProvider';
import { ColumnDef, DataTable } from '../Components/Table/DataTable';
import { Header } from '../Components/Header/Header';
import useGetCustomData from '../Hooks/useGetCustomData';
import { CompanySelect } from '../Components/Select/CompanySelect';
import { useNavigate } from 'react-router-dom';
import { PencilIcon } from 'lucide-react';

const toPayload = (formData: LocationFormInput) => ({
  name: formData.name.trim(),
  CID: Number(formData.CID),
  OVCODE: formData.OVCODE.trim(),
  CustomerName: formData.CustomerName.trim() || null,
  CustomerId: formData.CustomerId.trim() || null,
  ShipToId: formData.ShipToId.trim() || null,
  ContractJobNumber: formData.ContractJobNumber.trim() || null,
  SaleLocationId: formData.SaleLocationId.trim() || null,
  longitude: formData.longitude.trim() === '' ? null : Number(formData.longitude),
  latitude: formData.latitude.trim() === '' ? null : Number(formData.latitude),
  LocationTypeId: formData.LocationTypeId ? Number(formData.LocationTypeId) : null,
  p21_nc_post_order_flag: formData.p21_nc_post_order_flag,
  p21_nc_auto_order_approval_flag: formData.p21_nc_auto_order_approval_flag,
  binlocation_flag: formData.binlocation_flag,
  p21_nc_post_quote_flag: formData.p21_nc_post_quote_flag,
  IsActive: formData.IsActive,
});

const LocationPage: React.FC = () => {
  const navigate = useNavigate();
  const [showAddModal, setShowAddModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [locationToDelete, setCompanyToDelete] = useState<Location | null>(null);
  const [locationToEdit, setLocationToEdit] = useState<Location | null>(null);

  const token: any = decode();
  const isAbatixAdmin = token.roles.level >= 100;

  const { showSnackbar } = useSnackbar();

  const [companies] = useGetData<Company>('/api/company');
  const [CID] = useGetCustomData('/api/company/userregister');
  const [selectedCompany, setSelectedCompany] = useState<string>('');

  useEffect(() => {
    if (token.roles.id !== USER_ROLE.ADMIN_ABATIX && CID.length > 0 && !selectedCompany) {
      setSelectedCompany(CID[0].CID);
    }
  }, [token.roles.id, CID, selectedCompany]);

  const [data, loading, , refetch] = useGetData<Location>(`/api/location/${selectedCompany}`);

  const handleSubmit = async (formData: LocationFormInput) => {
    try {
      const payload = toPayload(formData);

      const response = await apiClient('/api/location/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        showSnackbar(errorData.error || 'Failed to add location', 'error');
        return;
      }

      const created = await response.json();
      showSnackbar(`${created.name} was successfully added.`, 'success', 'Successfully Added!');
      setShowAddModal(false);
      if (!selectedCompany) {
        setSelectedCompany(String(formData.CID));
      }
      refetch();
    } catch (err: any) {
      showSnackbar(err.message || 'Failed to add location', 'error');
    }
  };

  const handleEdit = async (formData: LocationFormInput) => {
    if (!locationToEdit) return;

    try {
      const payload: Partial<ReturnType<typeof toPayload>> = toPayload(formData);
      // The API only lets Abatix admins send IsActive; leaving it out keeps the stored value.
      if (!isAbatixAdmin) delete payload.IsActive;

      const response = await apiClient(`/api/location/${locationToEdit.OVID}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        showSnackbar(errorData.error || errorData.message || 'Failed to update location', 'error');
        return;
      }

      showSnackbar(`Successfully updated ${payload.name}`, 'success', 'Edit Success!');
      setLocationToEdit(null);
      refetch();
    } catch (err: any) {
      showSnackbar(err.message || 'Failed to update location', 'error');
    }
  };

  const handleToggleActive = async (location: Location) => {
    try {
      const response = await apiClient(`/api/location/${location.OVID}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...location, IsActive: !location.IsActive }),
      });

      if (!response.ok) {
        showSnackbar('Failed to update location', 'error');
        return;
      }

      showSnackbar(`Successfully updated ${location.name}`, 'success', 'Edit Success!');
      refetch();
    } catch (err: any) {
      showSnackbar(err.message || 'Failed to update location', 'error');
    }
  };

  const handleConfirmDelete = async () => {
    if (!locationToDelete) return;

    try {
      const response = await apiClient(`/api/location/${locationToDelete.OVID}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
      });

      if (!response.ok) {
        showSnackbar('Failed to delete location', 'error');
        return;
      }

      showSnackbar(`Successfully deleted ${locationToDelete.name}`, 'success');
      refetch();
    } catch (err: any) {
      showSnackbar(`${err.message ? err.message : 'Failed to delete'}`, 'error');
    } finally {
      setShowDeleteModal(false);
      setCompanyToDelete(null);
    }
  };

  const locationColumnsNew: ColumnDef<Location>[] = [
    {
      field: 'actions' as keyof Location,
      headerName: 'Actions',
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <div style={{ display: 'flex', gap: 5, alignItems: 'center', justifyContent: 'center' }}>
          <EditButton
            title="Edit location"
            onClick={(e) => {
              e.stopPropagation();
              setLocationToEdit(params.row);
            }}
          >
            <PencilIcon />
          </EditButton>
        </div>
      ),
    },
    { field: 'name', headerName: 'Location', width: 200, sortable: true, filterable: true, searchable: true },
    { field: 'OVCODE', headerName: 'Location ID', width: 150, sortable: true, filterable: true, searchable: true },
    { field: 'CustomerName', headerName: 'Account', width: 200, sortable: true, filterable: true, searchable: true },
    { field: 'CustomerId', headerName: 'Customer ID', width: 150, sortable: true, filterable: true },
    { field: 'ShipToId', headerName: 'Ship To ID', width: 150, sortable: true, filterable: true },
    { field: 'ContractJobNumber', headerName: 'Contract No.', width: 150, sortable: true, filterable: true, searchable: true },
    { field: 'SaleLocationId', headerName: 'Sale Location ID', width: 150, sortable: true, filterable: true },
    { field: 'longitude', headerName: 'Longitude', width: 130, sortable: true, filterable: true },
    { field: 'latitude', headerName: 'Latitude', width: 130, sortable: true, filterable: true },
    { field: 'LocationTypeId', headerName: 'Location Type ID', width: 150, sortable: true, filterable: true },
    { field: 'p21_nc_post_order_flag', headerName: 'Post Order', width: 120, type: 'boolean' },
    { field: 'p21_nc_auto_order_approval_flag', headerName: 'Auto Order Approval', width: 160, type: 'boolean' },
    { field: 'binlocation_flag', headerName: 'Bin Location', width: 130, type: 'boolean' },
    { field: 'p21_nc_post_quote_flag', headerName: 'Post Quote', width: 120, type: 'boolean' },
    { field: 'CreatedDate', headerName: 'Created', width: 120, sortable: true },
    { field: 'ModifiedDate', headerName: 'Modified', width: 120, sortable: true },
    { field: 'ModifiedBy', headerName: 'Modified By', width: 120, sortable: true },
    ...(isAbatixAdmin
      ? ([
          {
            field: 'IsActive' as keyof Location,
            headerName: 'Active',
            width: 100,
            renderCell: (params: any) => (
              <TealSwitch
                checked={params.row.IsActive}
                onChange={() => handleToggleActive(params.row)}
                color="primary"
              />
            ),
          },
        ] as ColumnDef<Location>[])
      : []),
    {
      field: 'OVID' as keyof Location,
      headerName: 'PO Presets',
      width: 150,
      renderCell: (params) => (
        <button
          onClick={() =>
            navigate(`/admin/location/${params.row.OVID}/po-presets`, {
              state: { locationName: params.row.name },
            })
          }
          style={{
            backgroundColor: '#00A1B1',
            color: 'white',
            border: 'none',
            borderRadius: 6,
            padding: '4px 12px',
            cursor: 'pointer',
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          PO Presets
        </button>
      ),
    },
  ];

  return (
    <PageContainer>
      <Header title="Location Management" data={data}>
        <AddButton onClick={() => setShowAddModal(true)}>+</AddButton>
        <BulkImportLink onClick={() => setShowImportModal(true)}>Bulk Import</BulkImportLink>
      </Header>

      {showAddModal && (
        <ModalOverlay onClick={() => setShowAddModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <CloseButton onClick={() => setShowAddModal(false)}>×</CloseButton>
            <ModalHeader>Add Location</ModalHeader>
            <LocationForm
              companies={companies}
              defaultCID={selectedCompany}
              existingLocations={data}
              onSubmit={handleSubmit}
              onCancel={() => setShowAddModal(false)}
            />
          </ModalContent>
        </ModalOverlay>
      )}

      {locationToEdit && (
        <ModalOverlay onClick={() => setLocationToEdit(null)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <CloseButton onClick={() => setLocationToEdit(null)}>×</CloseButton>
            <ModalHeader>Edit Location</ModalHeader>
            <LocationForm
              key={locationToEdit.OVID}
              companies={companies}
              initialData={locationToEdit}
              currentOvid={locationToEdit.OVID}
              existingLocations={data}
              canEditActive={isAbatixAdmin}
              onSubmit={handleEdit}
              onCancel={() => setLocationToEdit(null)}
            />
          </ModalContent>
        </ModalOverlay>
      )}

      {showImportModal && (
        <ModalOverlay onClick={() => setShowImportModal(false)}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <CloseButton onClick={() => setShowImportModal(false)}>×</CloseButton>
            <ModalHeader>Bulk Import</ModalHeader>
            <LocationBulkImport
              onClose={() => setShowImportModal(false)}
              onSuccess={() => refetch()}
            />
          </ModalContent>
        </ModalOverlay>
      )}

      {showDeleteModal && locationToDelete && (
        <ModalOverlay>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <CloseButton onClick={() => setShowDeleteModal(false)}>×</CloseButton>
            <ModalHeader>Confirm Deletion</ModalHeader>
            <p>
              Are you sure you want to delete the location: <strong>{locationToDelete.name}</strong>?
            </p>
            <div style={{ display: 'flex', justifyContent: 'center', gap: 5, alignItems: 'center' }}>
              <CancelButton onClick={() => setShowDeleteModal(false)}>Cancel</CancelButton>
              <DeleteConfirmButton onClick={handleConfirmDelete}>Delete</DeleteConfirmButton>
            </div>
          </ModalContent>
        </ModalOverlay>
      )}

      <DataTable
        columns={locationColumnsNew}
        data={data}
        loading={loading}
        pageKey="locations"
        defaultSort={[{ field: 'name', sort: 'asc' }]}
        idField="OVID"
      >
        <CompanySelect
          companies={CID}
          selectedCompany={selectedCompany}
          onChange={setSelectedCompany}
        />
      </DataTable>
    </PageContainer>
  );
};

export default LocationPage;
